import React, { useState, useEffect } from 'react'
import { Modal, Form, Input, InputNumber, AutoComplete, Spin } from 'antd'
import { LoadingOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons'
import { StyledButton } from '../../Button'
import { useCaseViewContext } from '../../../context'
import { useBiometrics } from './hooks/useBiometrics'
import { useAllergies } from './hooks/useAllergies'
import { AllergyTag } from './AllergyTag'

const { TextArea } = Input

export const PatientBiometricsModal = ({
    visible,
    onCancel,
    onSuccess,
    readOnlyMode = false,
    fetchPatientAllergies
}) => {
  const { apiClasses, apiType, caseDetails, fetchCaseDetails } = useCaseViewContext()
    const [form] = Form.useForm()
    const [loading, setLoading] = useState(false)
    const [searchValue, setSearchValue] = useState('')

    const {
        initialValues,
        handleSubmit,
        handleCancel: handleCancelInternal
    } = useBiometrics({
        caseDetails,
        form,
        setLoading,
        onSuccess: () => {
            if (fetchCaseDetails) {
                fetchCaseDetails()
            }

            getPatientAllergies()

            if (fetchPatientAllergies) {
                fetchPatientAllergies()
            }
            onSuccess?.()
        },
        apiClasses,
        readOnlyMode,
        apiType
    })

    const {
        setAllergySearchTerm,
        addAllergyToQueue,
        removeAllergyFromQueue,
        resetAllergies,
        allergySearchResultsLoading,
        allergySearchTerm,
        allergySearchResults,
        newAllergyQueue,
        patientAllergies,
        getPatientAllergies
    } = useAllergies({
        patientId: caseDetails?.patientId || caseDetails?.patient?._id,
        apiClasses
    })

    const handleAddAllergyToQueue = (value) => {
        const selectedAllergy = allergySearchResults.find(allergy => allergy.displayName === value)
        if (selectedAllergy) {
            addAllergyToQueue(selectedAllergy)
            setSearchValue('') 
        }
    }

    const handleSearchChange = (value) => {
        setSearchValue(value)
        setAllergySearchTerm(value)
    }

    const handleCancel = () => {
        resetAllergies()
        setSearchValue('')
        handleCancelInternal()
        onCancel?.()
    }

    const handleSubmitInternal = () => {
        const formValues = form.getFieldsValue()
        handleSubmit(formValues, newAllergyQueue, patientAllergies)
    }

    useEffect(() => {
        if (visible && initialValues) {
            form.setFieldsValue(initialValues)
            setSearchValue('')
            getPatientAllergies()
        }
    }, [visible, initialValues, form, getPatientAllergies])

    return (
        <Modal
            title="Edit Medical Information"
            open={visible}
            centered
            closable={false}
            width={600}
            footer={[
                <div key="footer" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <StyledButton
                        id="biometrics-cancel-btn"
                        Icon={CloseCircleOutlined}
                        onClick={handleCancel}
                        tooltip="Cancel"
                        color="red"
                        disabled={loading}
                    />
                    <StyledButton
                        id="biometrics-submit-btn"
                        Icon={CheckCircleOutlined}
                        onClick={handleSubmitInternal}
                        tooltip="Submit"
                        style={{ marginLeft: '4px'}}
                        disabled={loading}
                    />
                </div>
            ]}
        >
            <Spin spinning={loading} indicator={<LoadingOutlined />}>
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={initialValues}
                >
                    <Form.Item
                        label="Height (in)"
                        name="height"
                    >
                        <InputNumber 
                            id="biometrics-height-input"
                            min={0} 
                            max={120}
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                    
                    <Form.Item
                        label="Weight (lbs)"
                        name="weight"
                    >
                        <InputNumber 
                            id="biometrics-weight-input"
                            min={0} 
                            max={1000}
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                    
                    <Form.Item
                        label="Blood Pressure"
                        name="bloodPressure"
                    >
                        <Input id="biometrics-blood-pressure-input" placeholder="e.g., 120/80" />
                    </Form.Item>

                    <Form.Item
                        label="Medications"
                        name="medications"
                    >
                        <TextArea 
                            id="biometrics-medications-textarea"
                            autoSize={{ minRows: 3, maxRows: 6 }}
                            placeholder="List current medications..."
                        />
                    </Form.Item>
                    
                    <Form.Item
                        label="Patient-Reported Allergies"
                        name="allergies"
                    >
                        <TextArea 
                            id="biometrics-allergies-textarea"
                            autoSize={{ minRows: 3, maxRows: 6 }}
                            placeholder="List patient-reported allergies..."
                        />
                    </Form.Item>
                    
                    <Form.Item
                        label="Recorded Allergies"
                        name="recorded-allergies"
                    >
                        {readOnlyMode || apiType === 'admin' ? (
                            <div style={{ 
                                padding: '8px 12px', 
                                border: '1px solid #d9d9d9', 
                                borderRadius: '6px',
                                backgroundColor: '#f5f5f5',
                                color: '#666',
                                minHeight: '40px'
                            }}>
                                {patientAllergies?.length > 0 ? (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                        {patientAllergies.map(allergy => (
                                            <AllergyTag
                                                key={allergy.name}
                                                allergy={allergy}
                                                closable={false}
                                                handleDequeueAllergy={() => {}} // No-op for read-only
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    'No recorded allergies'
                                )}
                            </div>
                        ) : (
                            <div>
                                <AutoComplete
                                    id="biometrics-allergy-search-autocomplete"
                                    allowClear
                                    options={allergySearchResults
                                        .map(allergy => ({ value: allergy.displayName, label: allergy.displayName }))
                                        .filter(allergy => {
                                            const existingNames = [
                                                ...patientAllergies.map(pa => (pa.name || pa.displayName || '').toLowerCase().trim()),
                                                ...newAllergyQueue.map(na => (na.name || na.displayName || '').toLowerCase().trim())
                                            ]
                                            const searchValue = (allergy.value || '').toLowerCase().trim()
                                            
                                            if (existingNames.includes(searchValue)) {
                                                return true 
                                            }
                                            
                                            const baseName = searchValue
                                                .replace(/\s+(allergy|adverse\s+reaction)$/i, '')
                                                .trim()
                                            
                                            const hasRelatedAllergy = existingNames.some(existingName => {
                                                const existingBaseName = existingName
                                                    .replace(/\s+(allergy|adverse\s+reaction)$/i, '')
                                                    .trim()
                                                return existingBaseName === baseName && existingBaseName.length > 0
                                            })
                                            
                                            return !hasRelatedAllergy
                                        })
                                    }
                                    onSelect={handleAddAllergyToQueue}
                                    onSearch={handleSearchChange}
                                    value={searchValue}
                                    placeholder="Search for allergy to add"
                                    notFoundContent={allergySearchResultsLoading
                                        ? <Spin size="small" />
                                        : allergySearchTerm?.length ? "No matches" : "Type to search"}
                                    style={{ width: '100%' }}
                                />
                                
                                <div style={{ 
                                    display: 'flex', 
                                    flexWrap: 'wrap', 
                                    gap: '8px',
                                    marginTop: '8px',
                                    minHeight: '32px',
                                    padding: '4px 0'
                                }}>
                                    {patientAllergies?.map(allergy => (
                                        <AllergyTag
                                            key={`existing-${allergy.name || allergy.displayName}`}
                                            allergy={allergy}
                                            closable={false}
                                            handleDequeueAllergy={() => {}} 
                                        />
                                    ))}
                                    {newAllergyQueue?.map(allergy => (
                                        <AllergyTag
                                            key={`new-${allergy.name || allergy.displayName}`}
                                            allergy={allergy}
                                            closable={true}
                                            handleDequeueAllergy={removeAllergyFromQueue}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </Form.Item>
                </Form>
            </Spin>
        </Modal>
    )
}
